import {
    Color4,
    Mesh,
    MeshBuilder,
    ParticleSystem,
    StandardMaterial,
    Texture,
    Vector3,
} from "@babylonjs/core"
import { prettyJson } from "../../../common/CCUtils"
import { log } from "../../../common/Logging"
import { BabUtils } from "../../BabUtils"
import { MazeAudio } from "./assets/MazeAudio"
import { mazeImages } from "./assets/MazeImages"
import { Coor, MazeGrid } from "./MazeGrid"
import { MazeStore } from "./MazeStore"
import { Wall } from "./MazeWalls"

export interface Space extends Coor {
    cig?: boolean
    ecig?: boolean
    orientHorizontally?: boolean
    sightZone?: Coor
    moveUp?: boolean
}

interface CigInfo extends Space {
    mesh: Mesh
    smoked: boolean
}

export class MazeCigs {
    private static readonly y = 5

    static forTestingRealMaze = () =>
        new MazeCigs(
            [
                { x: 4, z: 0, cig: true, orientHorizontally: true },
                { x: 3, z: 0, cig: true },
            ],
            [],
            true,
        )

    static forTestingFakeMaze = () =>
        new MazeCigs([
            { x: 4, z: 0, cig: true, orientHorizontally: true, moveUp: true },
            { x: 3, z: 0, cig: true, moveUp: true },
        ])

    static forMazeRunner = () =>
        new MazeCigs([
            { x: 1, z: 1, ecig: true, orientHorizontally: true, moveUp: true },
            { x: 7, z: 1, cig: true, orientHorizontally: true, moveUp: true },
            { x: 4, z: 2, ecig: true, orientHorizontally: true, moveUp: true },
            { x: 1, z: 3, ecig: true, orientHorizontally: true, moveUp: true },
            { x: 5, z: 5, cig: true, orientHorizontally: true, moveUp: true },
            { x: 1, z: 6, ecig: true, orientHorizontally: true, moveUp: true },
            { x: 8, z: 6, cig: true, orientHorizontally: true, moveUp: true },
            { x: 7, z: 7, ecig: true, orientHorizontally: true, moveUp: true },
            { x: 2, z: 8, cig: true, orientHorizontally: true, moveUp: true },
        ])

    // Sorted by site zone x, then z ascending
    static forRealMaze = (walls: Array<Wall>) =>
        new MazeCigs(
            [
                { x: 1, z: 1, cig: true },
                { x: 0, z: 3, ecig: true, orientHorizontally: true },
                { x: 1, z: 6, cig: true },
                { x: 1, z: 8, ecig: true },
                { x: 0, z: 10, cig: true, orientHorizontally: true },
                { x: 1, z: 12, cig: true },
                { x: 0, z: 15, ecig: true, orientHorizontally: true },
                { x: 0, z: 16, cig: true, orientHorizontally: true },

                { x: 0, z: 13, cig: true },
                { x: 1, z: 14, ecig: true, orientHorizontally: true },
                { x: 1, z: 16, cig: true, orientHorizontally: true },
                { x: 0, z: 18, ecig: true },

                { x: 3, z: 4, cig: true },
                { x: 2, z: 12, ecig: true, orientHorizontally: true },
                { x: 2, z: 13, ecig: true, orientHorizontally: true },
                { x: 2, z: 17, cig: true, orientHorizontally: true },
                { x: 3, z: 18, cig: true },

                { x: 3, z: 2, ecig: true, orientHorizontally: true },
                { x: 4, z: 7, cig: true },
                { x: 3, z: 12, cig: true, orientHorizontally: true },
                { x: 3, z: 15, cig: true, orientHorizontally: true },
                { x: 4, z: 15, ecig: true },
                { x: 3, z: 17, cig: true, orientHorizontally: true },

                { x: 5, z: 2, cig: true },
                { x: 3, z: 8, cig: true },
                { x: 4, z: 10, ecig: true, orientHorizontally: true },
                { x: 5, z: 13, cig: true },
                { x: 4, z: 17, ecig: true, orientHorizontally: true },

                { x: 5, z: 17, ecig: true, orientHorizontally: true },

                { x: 5, z: 3, ecig: true },
                { x: 7, z: 5, ecig: true },
                { x: 7, z: 7, cig: true },
                { x: 6, z: 8, cig: true, orientHorizontally: true },
                { x: 6, z: 10, cig: true, orientHorizontally: true },
                { x: 6, z: 13, ecig: true, orientHorizontally: true },
                { x: 6, z: 15, cig: true, orientHorizontally: true },

                { x: 6, z: 2, ecig: true },

                { x: 7, z: 3, cig: true },
                { x: 7, z: 6, ecig: true },
                { x: 7, z: 8, ecig: true },
                { x: 8, z: 14, ecig: true, orientHorizontally: true },
                { x: 7, z: 17, cig: true },
                { x: 7, z: 18, cig: true },

                { x: 1, z: 19, cig: true },
                { x: 3, z: 19, ecig: true },
                { x: 5, z: 19, cig: true },
                { x: 7, z: 19, ecig: true },
            ],
            walls,
            true,
        )

    cigs: Array<CigInfo> = []

    private systems: Array<ParticleSystem> = []
    private cigMessageIdx = 0
    private ecigMessageIdx = 0
    private sizeMultiplier = 0.65
    private currentlySmoking = false

    private cigMessages = [
        "Wrong way! You have to smoke a cigarette and lose time.",
        "Dead end! It’s harder to get out of the maze than you thought.",
        "Wrong turn! Everyone makes mistakes, and you’re no different.",
        "No escape from these cigarettes! Just like everyone else you can’t escape from the maze of addiction.",
        "Oh no! You’re just like the teen who tried smoking. It’s easier to get addicted than you imagined.",
        "You’re just like everyone else. It’s easier to get addicted to smoking than you thought.",
    ]
    private ecigMessages = [
        "Wrong way! You have to vape an e-cigarette and lose time.",
        "Dead end! It’s harder to get out of the maze than you thought.",
        "Wrong turn! Everyone makes mistakes, and you’re no different.",
        "No escape from these e-cigarettes! Just like everyone else you can’t escape from the maze of addiction.",
        "Oh no! You’re just like the teen who tried tobacco. It’s easier to get addicted than you imagined.",
        "You’re just like everyone else. It’s easier to get addicted to vaping than you thought.",
    ]

    constructor(
        private cigCoors: Array<Space>,
        private walls?: Array<Wall>,
        private small?: boolean,
    ) {
        const { scene } = MazeStore.instance
        const { cigs, ecigs } = MazeAudio.instance

        if (walls != null) {
            cigCoors.forEach((cigCoor) => {
                const connectors = MazeGrid.findConnectingSpaces(cigCoor, walls)
                if (connectors.length === 0) {
                    throw new Error(
                        "Oh no! There was a cig coor with no connectors.",
                    )
                }

                cigCoor.sightZone = connectors[0]
            })
        }

        if (
            cigs.length !== this.cigMessages.length ||
            ecigs.length !== this.ecigMessages.length
        ) {
            throw new Error(
                "Cig and ecig message and audio array lengths must be equal.",
            )
        }

        if (small) {
            this.sizeMultiplier = 0.25
        }
        const sm = this.sizeMultiplier

        const cigMaterial = new StandardMaterial("cig-skin", scene)
        cigMaterial.ambientTexture = new Texture(mazeImages.cigSkin, scene)
        const ecigMaterial = new StandardMaterial("ecig-skin", scene)
        ecigMaterial.ambientTexture = new Texture(mazeImages.ecigSkin, scene)

        cigCoors.forEach((coor) => {
            if (coor.ecig || coor.cig) {
                const { x, z } = MazeGrid.getMidPositionOnGrid(coor)
                const { ecig, cig, orientHorizontally } = coor
                const cigCyl = MeshBuilder.CreateCylinder(
                    `cig${x}${z}`,
                    coor.ecig
                        ? { height: 6 * sm, diameter: 0.4 * sm }
                        : { height: 4 * sm, diameter: 0.6 * sm },
                    scene,
                )
                cigCyl.isPickable = false
                cigCyl.position.x = x
                cigCyl.position.z = z
                cigCyl.position.y = MazeCigs.y
                cigCyl.position = this.plusMoveUp(coor.moveUp, cigCyl.position)

                if (orientHorizontally) {
                    cigCyl.addRotation(
                        BabUtils.degreeToRadian(0),
                        0,
                        BabUtils.degreeToRadian(60),
                    )
                } else {
                    cigCyl.addRotation(BabUtils.degreeToRadian(60), 0, 0)
                }

                if (ecig) {
                    cigCyl.material = ecigMaterial
                } else if (cig) {
                    cigCyl.material = cigMaterial
                }

                this.cigs.push({ mesh: cigCyl, smoked: false, ...coor })
            }
        })
    }

    needsSmoking = async () => {
        if (this.currentlySmoking) {
            return
        }

        const { cameras, gui, scene, mazePathfinder } = MazeStore.instance
        const { buzzer, cigs, ecigs, sfxVolume } = MazeAudio.instance
        buzzer.setVolume(sfxVolume)

        const toSmoke = this.cigs.filter(
            (cigInfo) =>
                !cigInfo.smoked &&
                scene.isActiveMesh(cigInfo.mesh) &&
                MazeGrid.rayFromPlayerReachesCig(cigInfo.mesh),
        )
        if (toSmoke.length > 1) {
            log.error(
                `Whaaa? We should only find up to one cig that needs smoking. Or sight zone to use was null ${prettyJson(
                    toSmoke.map((smokable) => ({
                        x: smokable.x,
                        z: smokable.z,
                    })),
                )}`,
            )
        } else if (toSmoke.length === 1) {
            this.currentlySmoking = true
            const smokeThisUn = toSmoke[0]
            smokeThisUn.smoked = true

            cameras.removeFreeCameraInputs()

            gui.stopTimer()

            buzzer.play()

            const freeCameraCoor = MazeGrid.getCoorForVectorPosition(
                cameras.freeCamera.position,
            )

            const pathToSightZoneCoors = mazePathfinder(
                freeCameraCoor,
                smokeThisUn.sightZone,
            )
            const pathToSightZone = pathToSightZoneCoors.map((coor) =>
                MazeGrid.getMidPositionOnGrid(coor),
            )

            await cameras.targetFreeCameraAtAndMoveThrough(
                smokeThisUn.mesh.position,
                pathToSightZone,
            )

            let audioFile
            let message
            if (smokeThisUn.ecig) {
                audioFile = ecigs[this.ecigMessageIdx]
                message = this.ecigMessages[this.ecigMessageIdx]
                this.ecigMessageIdx++
                if (this.ecigMessageIdx > ecigs.length - 1) {
                    this.ecigMessageIdx = 0
                }
            } else {
                audioFile = cigs[this.cigMessageIdx]
                message = this.cigMessages[this.cigMessageIdx]
                this.cigMessageIdx++
                if (this.cigMessageIdx > cigs.length - 1) {
                    this.cigMessageIdx = 0
                }
            }

            gui.addGuiText(message)
            await audioFile()
            gui.startTimer()

            this.addSmokeAt(smokeThisUn)
            gui.startSmokeTimer(() => {
                cameras.addFreeCameraInputs()
                this.stopSmoke()
                smokeThisUn.mesh.dispose()
                gui.removeGuiText()
                this.currentlySmoking = false
            }, smokeThisUn.ecig)
        }
    }

    addSmokeAt = (cigCoor: Space) => {
        const { x, z, cig, ecig } = cigCoor
        log.debug(`Smoking at ${x},${z}`)
        let particleAssets
        if (ecig) {
            particleAssets = mazeImages.puffs
        } else if (cig) {
            particleAssets = mazeImages.wisps
        }

        particleAssets.forEach((wisp) => {
            this.addSmokeParticle(
                new Texture(wisp, MazeStore.instance.scene),
                cigCoor,
                MazeCigs.y,
                ecig,
            )
        })
    }

    stopSmoke = () =>
        this.systems.forEach((system) => {
            system.emitRate = 0
        })

    destroy = () => {
        this.systems.forEach((system) => system.dispose())
        this.cigs.forEach((cig) => cig.mesh.dispose())
    }

    private addSmokeParticle = (
        texture: Texture,
        coor: Space,
        y: number,
        ecig?: boolean,
    ) => {
        log.debug("Adding particle system.")
        const sm = this.sizeMultiplier
        const { x, z } = MazeGrid.getMidPositionOnGrid(coor)
        const particleSystem = new ParticleSystem(
            "smoke",
            2,
            MazeStore.instance.scene,
        )
        const boxSize = 0.25 * sm

        let yMod
        let horizontalXMod
        if (ecig) {
            yMod = 3 * sm
            horizontalXMod = 4 * sm
        } else {
            yMod = 2 * sm
            horizontalXMod = 2 * sm
        }
        let emitterVector
        if (coor.orientHorizontally) {
            log.debug("Particle for horizontal.")
            emitterVector = new Vector3(x - horizontalXMod, y + yMod, z)
        } else {
            emitterVector = new Vector3(x, y + yMod, z + horizontalXMod)
        }

        log.debug(`Move up particle true? ${coor.moveUp}`)
        particleSystem.emitter = this.plusMoveUp(coor.moveUp, emitterVector)

        if (!ecig) {
            this.cigGlow(particleSystem.emitter, coor)
        }

        particleSystem.minEmitBox = new Vector3(-boxSize, -boxSize, -boxSize)
        particleSystem.maxEmitBox = new Vector3(boxSize, boxSize, boxSize)
        particleSystem.particleTexture = texture

        particleSystem.addColorGradient(0, new Color4(1, 1, 1, 0))
        particleSystem.addColorGradient(0.25, new Color4(1, 1, 1, 1))
        particleSystem.addColorGradient(1, new Color4(1, 1, 1, 0))

        let sizeSm = this.small ? sm : sm * 2.5
        if (ecig) {
            sizeSm *= 2
        }

        particleSystem.minSize = 2 * sizeSm
        particleSystem.maxSize = 3 * sizeSm
        particleSystem.minLifeTime = 1
        particleSystem.maxLifeTime = 3
        particleSystem.emitRate = 2 + Math.random()
        particleSystem.blendMode = ParticleSystem.BLENDMODE_STANDARD
        particleSystem.gravity = new Vector3(0, sm, 0)
        particleSystem.direction1 = new Vector3(0, 0, 0)
        particleSystem.minEmitPower = 0.1
        particleSystem.maxEmitPower = 0.2
        particleSystem.updateSpeed = 0.005

        particleSystem.start()
        this.systems.push(particleSystem)
    }

    private cigGlow = (smokeEmitterVector: Vector3, coor: Space) => {
        const { cigLight } = mazeImages
        const { scene } = MazeStore.instance

        const sm = this.sizeMultiplier
        log.debug("Adding particle system.")
        const particleSystem = new ParticleSystem(
            "smoke",
            1000,
            MazeStore.instance.scene,
        )
        const boxSize = 0.01 * sm

        const { x, y, z } = smokeEmitterVector

        let cigGlowX = x
        let cigGlowY = y
        const cigGlowZ = z
        if (this.small) {
            cigGlowY = y - 0.95 * sm
            if (coor.orientHorizontally) {
                cigGlowX = x + 0.15 * sm
            }
        }

        particleSystem.emitter = new Vector3(cigGlowX, cigGlowY, cigGlowZ)
        particleSystem.minEmitBox = new Vector3(-boxSize, -boxSize, -boxSize)
        particleSystem.maxEmitBox = new Vector3(boxSize, boxSize, boxSize)
        particleSystem.particleTexture = new Texture(cigLight, scene)

        particleSystem.addColorGradient(
            0,
            new Color4(1, 0.3, 0, 0.3),
            new Color4(1, 0.1, 0, 0.6),
        )
        particleSystem.addColorGradient(
            1,
            new Color4(1, 0.3, 0, 0),
            new Color4(1, 0.1, 0, 0),
        )

        particleSystem.minSize = 0.9 * sm
        particleSystem.maxSize = 1.35 * sm
        particleSystem.minLifeTime = 0.05
        particleSystem.maxLifeTime = 0.1
        particleSystem.emitRate = 300
        particleSystem.blendMode = ParticleSystem.BLENDMODE_STANDARD
        particleSystem.maxEmitPower = 0.001
        particleSystem.gravity = new Vector3(0, 0, 0)
        particleSystem.updateSpeed = 0.001

        particleSystem.start()
        this.systems.push(particleSystem)
    }

    private plusMoveUp = (moveUp: boolean, vector: Vector3) =>
        moveUp
            ? new Vector3(vector.x - 0.9, vector.y + 0.5, vector.z + 2.1)
            : vector
}
